'use client';
import React from 'react';
import { VerticalType } from '../../types/cmsTypes';
import { CmsClientVerticalScope } from './CmsClientVerticalScope';
import { useCmsClientContext } from './useCmsClientContext';

type VerticalProp = { vertical?: VerticalType | undefined };

/**
 * Product-block wrapper: resolves the block's vertical from its own props
 * (falling back to the page vertical), passes it as `vertical` and scopes
 * CmsClientContext to it. Call at module scope only — a HOC built inside a
 * render is a new component type each pass and remounts the subtree.
 */
export function withVerticalScope<P extends VerticalProp>(
  Component: React.ComponentType<P>,
  resolveVertical: (props: Omit<P, 'vertical'>) => VerticalType | undefined,
): React.FC<Omit<P, 'vertical'>> {
  const Wrapped: React.FC<Omit<P, 'vertical'>> = (props) => {
    const { vertical: pageVertical } = useCmsClientContext();
    const vertical = resolveVertical(props) ?? pageVertical;

    return (
      <CmsClientVerticalScope vertical={vertical}>
        {/* TS cannot see that Omit<P, 'vertical'> + vertical is P. */}
        <Component {...(props as P)} vertical={vertical} />
      </CmsClientVerticalScope>
    );
  };
  Wrapped.displayName = `withVerticalScope(${Component.displayName ?? Component.name})`;

  return Wrapped;
}
